import { Component, h, escapeHtml, formatWhen, register } from './core.js';
import { renderIcon, iconHtml } from './icons.js';

/**
 * Timeline: a vertical list of dated events.
 *
 * new dfc.Timeline(container, {
 *   items: [{ id, at, title, body, icon, tone: 'neutral'|'success'|'warning'|'danger'|'info', meta }],   // icon: any icon spec
 *   order: 'desc' | 'asc', groupByDay: true, dense: false, html: false,
 *   renderItem: null | (item) => HTMLElement | string, maxItems: 500, locale: undefined
 * })
 * An item whose `at` is null, undefined, '' or an invalid date is undated: it
 * renders with no day header and an empty time cell, and sorts after every dated
 * item in both orders (keeping input order among undated items). `item.timeLabel`
 * replaces the time cell text and `item.dayLabel` the day header text.
 * Events: select { item }
 * Methods: add(item), remove(id), setItems(items), scrollTo(id)
 * Static: dfc.Timeline.renderStatic(items, opts) -> HTML string
 */
export class Timeline extends Component {
	static get componentName() { return 'timeline'; }

	defaults() {
		return { items: [], order: 'desc', groupByDay: true, dense: false, html: false, renderItem: null, maxItems: 500, locale: undefined, emptyText: 'Nothing to show yet.' };
	}

	render() {
		const o = this.options;
		this._items = Timeline._sorted(o.items, o.order);
		this.el.className = 'dfc dfc-timeline' + (o.dense ? ' is-dense' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		if (!this._items.length) {
			this.el.appendChild(h('div', { className: 'dfc-timeline-empty', text: o.emptyText }));
			return;
		}
		const list = h('ol', { className: 'dfc-timeline-list', role: 'list' });
		const selectable = this._handlers.has('select') && this._handlers.get('select').size > 0;
		let lastDay = null;
		const shown = this._items.slice(0, o.maxItems);
		shown.forEach((item) => {
			const when = Timeline._undated(item) ? { date: '', time: '', iso: '' } : formatWhen(item.at, o.locale);
			if (o.groupByDay && when.dayKey != null && when.dayKey !== lastDay) {
				list.appendChild(h('li', { className: 'dfc-timeline-day', role: 'presentation' }, h('span', { text: item.dayLabel != null ? String(item.dayLabel) : when.date })));
				lastDay = when.dayKey;
			}
			list.appendChild(this._renderItem(item, when, selectable));
		});
		if (this._items.length > shown.length) {
			list.appendChild(h('li', { className: 'dfc-timeline-more', role: 'presentation' },
				h('button', { type: 'button', className: 'dfc-btn dfc-btn-ghost', text: `Show ${this._items.length - shown.length} more`, onClick: () => this.update({ maxItems: o.maxItems + 500 }) })));
		}
		this.el.appendChild(list);
	}

	_renderItem(item, when, selectable) {
		const o = this.options;
		let body;
		if (typeof o.renderItem === 'function') {
			const out = o.renderItem(item);
			body = out instanceof Node ? out : h('div', { className: 'dfc-timeline-body', [o.html ? 'html' : 'text']: out || '' });
		} else {
			body = h('div', { className: 'dfc-timeline-body' },
				item.title ? h('div', { className: 'dfc-timeline-title', text: item.title }) : null,
				item.body !== undefined && item.body !== null && item.body !== '' ? h('div', { className: 'dfc-timeline-text', [o.html ? 'html' : 'text']: item.body }) : null);
		}
		const dot = h('span', { className: 'dfc-timeline-dot', 'aria-hidden': 'true' }, renderIcon(item.icon || (item.iconCss ? { className: item.iconCss } : null)));
		return h('li', {
			className: `dfc-timeline-item is-${item.tone || 'neutral'}`, dataset: { id: String(item.id) }, role: 'listitem',
			tabindex: selectable ? '0' : null, title: when.iso ? when.date + ' ' + when.time : null,
		}, h('span', { className: 'dfc-timeline-when' }, h('span', { className: 'dfc-timeline-time', text: item.timeLabel != null ? String(item.timeLabel) : when.time }), o.groupByDay ? null : h('span', { className: 'dfc-timeline-date', text: when.date })), dot, body);
	}

	bind() {
		this._onClick = (e) => {
			const li = e.target.closest && e.target.closest('.dfc-timeline-item');
			if (!li || !this.el.contains(li)) return;
			const item = this._items.find((x) => String(x.id) === li.dataset.id);
			if (item) this.emit('select', { item });
		};
		this._onKey = (e) => {
			if (e.key !== 'Enter' && e.key !== ' ') return;
			const li = e.target.closest && e.target.closest('.dfc-timeline-item');
			if (!li) return;
			e.preventDefault();
			const item = this._items.find((x) => String(x.id) === li.dataset.id);
			if (item) this.emit('select', { item });
		};
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('keydown', this._onKey);
	}

	unbind() {
		if (!this.el) return;
		this.el.removeEventListener('click', this._onClick);
		this.el.removeEventListener('keydown', this._onKey);
	}

	get items() { return this._items.slice(); }

	setItems(items) { return this.update({ items: Array.isArray(items) ? items.slice() : [] }); }

	add(item) {
		const items = this.options.items.filter((x) => String(x.id) !== String(item.id));
		items.push(item);
		this.update({ items });
		const li = this.el.querySelector(`.dfc-timeline-item[data-id="${CSS.escape(String(item.id))}"]`);
		if (li) li.classList.add('is-new');
		return this;
	}

	remove(id) {
		return this.update({ items: this.options.items.filter((x) => String(x.id) !== String(id)) });
	}

	scrollTo(id) {
		const li = this.el.querySelector(`.dfc-timeline-item[data-id="${CSS.escape(String(id))}"]`);
		if (li && li.scrollIntoView) li.scrollIntoView({ block: 'center', behavior: 'smooth' });
		return this;
	}

	/** True when an item has no usable timestamp (null, missing, empty string or invalid). */
	static _undated(item) {
		const at = item ? item.at : null;
		if (at === null || at === undefined || at === '') return true;
		const d = at instanceof Date ? at : new Date(at);
		return isNaN(d.getTime());
	}

	static _sorted(items, order) {
		const t = (x) => { const d = x.at instanceof Date ? x.at : new Date(x.at); return d.getTime(); };
		return (items || []).slice().sort((a, b) => {
			const au = Timeline._undated(a), bu = Timeline._undated(b);
			if (au || bu) return au === bu ? 0 : au ? 1 : -1;
			return order === 'asc' ? t(a) - t(b) : t(b) - t(a);
		});
	}

	/** Read-only HTML (text-only bodies unless opts.html is true). */
	static renderStatic(items, opts = {}) {
		const sorted = Timeline._sorted(items, opts.order || 'desc');
		let html = `<div class="dfc dfc-timeline is-static${opts.dense ? ' is-dense' : ''}"><ol class="dfc-timeline-list" role="list">`;
		let lastDay = null;
		sorted.forEach((item) => {
			const when = Timeline._undated(item) ? { date: '', time: '' } : formatWhen(item.at, opts.locale);
			if (opts.groupByDay !== false && when.dayKey != null && when.dayKey !== lastDay) { html += `<li class="dfc-timeline-day" role="presentation"><span>${escapeHtml(item.dayLabel != null ? String(item.dayLabel) : when.date)}</span></li>`; lastDay = when.dayKey; }
			const body = opts.html ? String(item.body || '') : escapeHtml(item.body || '');
			const time = item.timeLabel != null ? String(item.timeLabel) : when.time;
			html += `<li class="dfc-timeline-item is-${escapeHtml(item.tone || 'neutral')}" role="listitem"><span class="dfc-timeline-when"><span class="dfc-timeline-time">${escapeHtml(time)}</span></span><span class="dfc-timeline-dot" aria-hidden="true">${iconHtml(item.icon || (item.iconCss ? { className: item.iconCss } : null))}</span><div class="dfc-timeline-body">${item.title ? `<div class="dfc-timeline-title">${escapeHtml(item.title)}</div>` : ''}${body ? `<div class="dfc-timeline-text">${body}</div>` : ''}</div></li>`;
		});
		return html + '</ol></div>';
	}
}

register('timeline', Timeline);
